import { z } from 'zod';
import { cuid, isoDate } from './primitives.js';

/**
 * Reforma tributaria (EC 132/2023, LC 214/2025): SPEC-PRICING §12.10. O regime sai da DATA DA OPERACAO; aliquotas e fatores de transicao sao
 * dado versionado (nunca codigo). Aliquotas de CBS/IBS de 2027 em diante sao HIPOTETICAS (`estimada`) ate a publicacao oficial.
 */
export type RegimeReforma = 'LEGACY' | 'DUAL_2026' | 'TRANSICAO' | 'NOVO_2033';

export interface TributoLinhaResponse {
  tributo: string;
  classe: 'por_dentro' | 'por_fora';
  base: string;
  /** Taxa EFETIVA sobre a base (ICMS com CBS/IBS na base: a_ef sobre P_d). */
  rate: string;
  /** ICMS/ISS/PIS/COFINS/IPI: alíquota cadastrada e fator do ano. */
  legalRate: string | null;
  factor: string | null;
  value: string;
  /** 2026: destacado, nao soma ao total (NT 2025.002). */
  informative: boolean;
  estimada: boolean;
}

export interface TributosPrecoResponse {
  /** P_d: valor da operacao sem tributos por fora. */
  precoSemTributos: string;
  cbsIbsBase: string;
  tributos: TributoLinhaResponse[];
  /** "+ CBS/IBS": soma dos CBS/IBS nao informativos (o IS, se houver, vem em `tributos`). */
  cbsIbs: string;
  /** Total da NF do item (P_d + IPI + por fora nao informativos). */
  totalNf: string;
  multiplicador: string;
  /** Soma de CBS/IBS que um adquirente do regime regular credita. */
  creditavelPeloCliente: string;
}

export interface ReformaFormacaoResponse {
  data: string;
  regime: RegimeReforma;
  etapa: string;
  /** CBS/IBS destacados mas fora do total (2026). */
  informativo: boolean;
  /** Alguma aliquota usada e hipotetica: a tela mostra "aliquota de referencia estimada". */
  aliquotaEstimada: boolean;
  precoIncluiCbsIbs: boolean;
  /** Parametro "CBS/IBS na base do ICMS" vigente (so vale na transicao). */
  cbsIbsNaBaseIcms: boolean;
  /** QT18: parametro "CBS/IBS na base do ISS" vigente (so vale na transicao). */
  cbsIbsNaBaseIss: boolean;
  sugerido: TributosPrecoResponse;
  praticado: TributosPrecoResponse | null;
  /** Avisos do cadastro (ex.: `TRIBUTO_NAO_MARCADO`: despesa tributo da tabela sem marcar ICMS/ISS/PIS/COFINS). */
  avisos: Array<{ code: string; message: string }>;
}

/** POST /precificacao/por-regime: o mesmo item/tabela/condicao (e praticado) em cada etapa da reforma ("este preco em 2027"). */
export const porRegimeSchema = z.object({
  itemId: cuid,
  tabelaId: cuid,
  condicaoId: cuid,
  data: isoDate.optional(),
  praticado: z.string().regex(/^\d+(\.\d{1,6})?$/, 'Informe um valor como 25.00').optional(),
  /** Sessao 37: simula com outra ficha do item (so exibicao; ver `precificarSchema.fichaId`). */
  fichaId: cuid.optional(),
});
export type PorRegimeDto = z.infer<typeof porRegimeSchema>;

export interface PorRegimeEtapaResponse {
  etapa: string;
  regime: RegimeReforma;
  /** Primeiro dia da etapa (data da operacao simulada). */
  data: string;
  atual: boolean;
  sugerido: TributosPrecoResponse;
  praticado: TributosPrecoResponse | null;
  /** Lucro simulado (fracao) do praticado nesta etapa, com f e l atuais. */
  lucroSimulado: string | null;
  lucroSimuladoValor: string | null;
  aliquotaEstimada: boolean;
  /** Erro do motor nesta etapa (ex.: divisor <= 0 com f e l atuais). */
  erro?: { code: string; message: string };
}
export interface PorRegimeResponse {
  itemId: string;
  etapas: PorRegimeEtapaResponse[];
}

/** Warnings novos do motor/API da reforma. */
export const REFORMA_AVISOS = ['TRIBUTO_NAO_MARCADO', 'ORCAMENTO_CRUZA_REGIME'] as const;

// ---------- Etapas e sugestoes de f e l (P1) ----------

export interface EtapaReformaResponse {
  id: string;
  stageLabel: string;
  regime: RegimeReforma;
  validFrom: string;
  validTo: string | null;
  fonte: string;
}

/** GET /reforma/etapas: o cronograma (dado versionado) e a fronteira que a proxima sugestao de f/l cobre. */
export interface ReformaEtapasResponse {
  hoje: string;
  etapaAtual: string;
  etapas: EtapaReformaResponse[];
  fronteiras: Array<{ validFrom: string; stageLabel: string; regime: RegimeReforma; anterior: RegimeReforma }>;
  /** Primeira fronteira ainda por vir (a sugestao e gerada para ela). */
  proximaFronteira: { validFrom: string; stageLabel: string } | null;
}

export interface SugestaoFlProduto {
  itemId: string;
  codigo: string;
  descricao: string;
  /** Lucro em R$ por unidade (base a vista): no regime anterior; no regime novo com f/l atuais (se recusada); com a sugestao. */
  lucroAntes: string;
  lucroSeRecusada: string;
  lucroComSugestao: string;
  /** Valor da operacao (P_d a vista) nas tres situacoes. */
  precoAntes: string;
  precoSeRecusada: string;
  precoComSugestao: string;
}

export interface SugestaoFlPayload {
  /** Preenchido quando nao ha sugestao (ex.: sem vendas aprovadas nos ultimos 12 meses). */
  semSugestao?: string;
  fixedExpenseRate?: { atual: string; sugerida: string; alvoRs: string; alcancadoRs: string };
  tabelas?: Array<{ tabelaId: string; tabelaCodigo: string; atual: string; sugerida: string; minimoAtual: string | null; /** QT19: o minimo acompanha o desejado pelo MESMO fator (mantem a proporcao). */ minimoSugerido: string | null; alvoRs: string; alcancadoRs: string }>;
  produtos?: SugestaoFlProduto[];
  amostra: { periodo: { de: string; ate: string }; orcamentos: number; linhas: number; tabelasForaDoMix: number };
  avisos: string[];
}

export interface SugestaoFlResponse {
  id: string;
  etapa: string;
  validFrom: string;
  status: 'PENDENTE' | 'APROVADA' | 'RECUSADA';
  geradaEm: string;
  decididaEm: string | null;
  observacao: string | null;
  payload: SugestaoFlPayload;
}

export const gerarSugestaoFlSchema = z.object({ validFrom: isoDate.optional() });
export type GerarSugestaoFlDto = z.infer<typeof gerarSugestaoFlSchema>;
const taxaFl = z.string().regex(/^(0|1)(\.\d{1,8})?$|^0?\.\d{1,8}$/, 'Taxa invalida (fracao, ate 8 casas)');
export const aprovarSugestaoFlSchema = z.object({
  /** "Editar e aprovar": valores diferentes da sugestao (padrao = a sugerida). */
  fixedExpenseRate: taxaFl.optional(),
  tabelas: z.array(z.object({ tabelaId: cuid, desiredProfitRate: taxaFl, /** QT19: sem ele, o minimo segue o desejado pelo mesmo fator. */ minimumProfitRate: taxaFl.nullable().optional() })).max(200).optional(),
  observacao: z.string().trim().max(300).optional(),
});
export type AprovarSugestaoFlDto = z.infer<typeof aprovarSugestaoFlSchema>;
export const recusarSugestaoFlSchema = z.object({ observacao: z.string().trim().max(300).optional() });
export type RecusarSugestaoFlDto = z.infer<typeof recusarSugestaoFlSchema>;

// ---------- Despesas a revisar (marca de tributo, Sessao 15c) ----------

/** Despesa variavel do tipo TRIBUTO, na vigencia vigente da tabela, ainda sem marca (ICMS, ISS, PIS, COFINS ou OUTRO). */
export interface DespesaARevisarResponse {
  tabelaId: string;
  tabelaCodigo: string;
  tabelaNome: string;
  vigenciaId: string;
  vigenteDesde: string;
  ordem: number;
  descricao: string;
  taxa: string;
  /** Marca que a descricao sugere (null = nao reconhecida ou ambigua). */
  sugestao: 'ICMS' | 'ISS' | 'PIS' | 'COFINS' | 'OUTRO' | null;
}

// ---------- Aliquota do IBS por destino (QT20, Sessao 15c) ----------

/** De 2029 em diante o IBS e do destino (UF, municipio opcional). Em 2027-2028 vale a lei: 0,05% estadual + 0,05% municipal, sem destino. */
export const PRIMEIRO_DIA_IBS_POR_DESTINO = '2029-01-01';

export const criarAliquotaDestinoSchema = z
  .object({
    uf: z.string().regex(/^[A-Z]{2}$/, 'UF com 2 letras maiusculas'),
    municipio: z.string().trim().min(1).max(100).optional(),
    /** Fracoes (0,0885 = 8,85%); o preco usa a soma, o rateio so aparece no detalhe. */
    ibsUf: taxaFl,
    ibsMun: taxaFl,
    estimada: z.boolean().default(true),
    validFrom: isoDate,
    validTo: isoDate.optional(),
    fonte: z.string().trim().min(1).max(300),
  })
  .refine((v) => v.validFrom >= PRIMEIRO_DIA_IBS_POR_DESTINO, { message: 'A aliquota por destino vale a partir de 2029-01-01 (antes, a lei fixa 0,05% + 0,05%)', path: ['validFrom'] })
  .refine((v) => !v.validTo || v.validTo > v.validFrom, { message: 'O fim da vigencia deve ser depois do inicio', path: ['validTo'] });
export type CriarAliquotaDestinoDto = z.infer<typeof criarAliquotaDestinoSchema>;

export interface AliquotaDestinoResponse {
  id: string;
  uf: string;
  municipio: string | null;
  ibsUf: string;
  ibsMun: string;
  /** ibsUf + ibsMun: o que entra no preco. */
  total: string;
  estimada: boolean;
  validFrom: string;
  validTo: string | null;
  fonte: string;
  recordedAt: string;
}
